import { useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { PROFILE_PUBLIC_COLUMNS } from "@/lib/profileColumns";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import Navbar from "@/components/Navbar";
import AuthMarketingPanel from "@/components/auth/AuthMarketingPanel";
import AuthFormCard from "@/components/auth/AuthFormCard";
import LogoWatermark from "@/components/LogoWatermark";
import Seo from "@/components/Seo";

const REQUIRED_PARENT_KEYS = ["full_name", "city", "street_address", "bio"] as const;

// Mise en production du nouveau tunnel PRO. Les comptes créés avant cette date
// sont des comptes existants : ils conservent toujours l'accès à leur espace.
const LEGACY_PRO_CUTOFF = new Date("2026-09-01T11:55:55Z").getTime();

const Auth = () => {
  const [searchParams] = useSearchParams();
  const { user, role, loading, subscriptionActive } = useAuth();
  const { getSetting } = useSiteSettings();
  const navigate = useNavigate();
  const initialTab = searchParams.get("tab") === "register" ? "register" : "login";
  const initialRole = searchParams.get("role") === "pro" ? "pro" : "parent";
  const resumeOnboarding = searchParams.get("resume") === "1";
  const subscriptionRequired = getSetting("pro_subscription_enabled", "true") !== "false";

  useEffect(() => {
    if (loading || !user || !role) return;

    const isLegacyPro = role === "pro" && Boolean(user.created_at) && new Date(user.created_at).getTime() < LEGACY_PRO_CUTOFF;

    // Un ancien PRO ne doit jamais être prisonnier du nouveau tunnel d'inscription.
    // Pour les nouvelles inscriptions, on conserve le mécanisme de reprise du tunnel.
    if (resumeOnboarding && !isLegacyPro) return;

    let cancelled = false;

    const decideRedirect = async () => {
      if (role === "admin") {
        if (!cancelled) navigate("/admin", { replace: true });
        return;
      }

      if (role === "pro" && isLegacyPro) {
        try {
          localStorage.removeItem("clickandgard_pro_onboarding_resume");
          localStorage.removeItem("clickandgard_pro_onboarding_draft");
        } catch {
          // Le stockage local peut être indisponible.
        }
        if (!cancelled) navigate("/pro/dashboard", { replace: true });
        return;
      }

      const [{ data: profile }, { data: phone }, { data: privateProfile }] = await Promise.all([
        // PROFILE_PUBLIC_COLUMNS est une sélection dynamique partagée ; Supabase ne peut pas l'inférer statiquement ici.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        supabase.from("profiles").select(PROFILE_PUBLIC_COLUMNS as any).eq("user_id", user.id).maybeSingle<any>(),
        supabase.rpc("get_my_phone"),
        supabase.rpc("get_my_private_profile"),
      ]);
      const privateRow = Array.isArray(privateProfile) ? privateProfile[0] : privateProfile;

      if (role === "pro") {
        // Nouveau compte PRO : l'abonnement est prioritaire après la création du compte.
        // Le profil essentiel et la vérification d'identité sont finalisés ensuite.
        const essentialMissing = !profile?.full_name?.trim() || !profile?.avatar_url || !phone || !privateRow?.street_address;
        if (subscriptionRequired && !subscriptionActive) {
          try { localStorage.setItem("clickandgard_pro_onboarding_resume", "true"); } catch { /* ignore */ }
          if (!cancelled) navigate("/auth?tab=register&role=pro&resume=1", { replace: true });
        } else if (essentialMissing) {
          try { localStorage.removeItem("clickandgard_pro_onboarding_resume"); } catch { /* ignore */ }
          if (!cancelled) navigate("/settings?tab=profile", { replace: true });
        } else if (!cancelled) {
          navigate("/pro/dashboard", { replace: true });
        }
        return;
      }

      const merged: Record<string, unknown> = { ...(profile ?? {}), ...(privateRow ?? {}), phone: phone ?? null };
      const missing = REQUIRED_PARENT_KEYS.some((key) => {
        const value = merged[key];
        return value === null || value === undefined || (typeof value === "string" && !value.trim());
      }) || !phone;
      if (!cancelled) navigate(missing ? "/settings?tab=profile" : "/search", { replace: true });
    };

    void decideRedirect();
    return () => { cancelled = true; };
  }, [user, role, loading, navigate, resumeOnboarding, subscriptionActive, subscriptionRequired]);

  return (
    <div className="min-h-screen w-full bg-immersive">
      <Seo title={initialTab === "register" ? "Créer un compte" : "Connexion"} description="Connectez-vous ou créez votre compte Click&Gard pour trouver une garde d'enfants vérifiée ou rejoindre notre réseau de professionnels." noIndex />
      <Navbar />
      <div className="w-full min-h-[calc(100vh-4rem)] flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-[52%] xl:w-[55%]"><AuthMarketingPanel /></div>
        <div className="flex-1 flex items-center justify-center px-4 py-10 sm:py-14 lg:py-8 relative overflow-hidden">
          <div className="neon-orb neon-orb-violet w-[350px] h-[350px] -top-[120px] -right-[100px]" />
          <div className="neon-orb neon-orb-pink w-[280px] h-[280px] -bottom-[80px] -left-[60px]" />
          <LogoWatermark position="center" opacity={26} size={26} />
          <div className="lg:hidden absolute top-0 left-0 right-0 bg-gradient-to-b from-primary/6 to-transparent h-32 pointer-events-none" />
          <AuthFormCard initialTab={initialTab} initialRole={initialRole} resumeOnboarding={resumeOnboarding} />
        </div>
      </div>
    </div>
  );
};

export default Auth;
